import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useContext, useMemo, useState } from 'react';
import { useToastNotification, useLocalizeMessage } from 'libs.nucleus';
import { OrgContext } from '../../contexts/org/org.context';
import { SiteContext } from '../../contexts/site';
import { UserLocaleContext } from '../../contexts/user_locale/user_locale.context';
import { getApiInstance, getApiErrorMessage } from '../../helpers/api.helpers';
import { PARTICIPANT_NUMBER_PREFIX } from '../../helpers/constants.helpers';
import { LUMOS_PERMISSIONS } from '../../types/permissions.types';
import { useCurrentEnvironment } from '../use_current_environment';
import { useLumosAuth } from '../use_lumos_auth';
// `/` is escaped alongside the usual set because it delimits cortex's `/pattern/flags` syntax.
const escapeRegExp = (value) => value.replace(/[/.*+?^${}()|[\]\\]/g, '\\$&');
/**
 * Which fields the search may reference is decided by Axon: `c_search` (which embeds the email) and `c_username`
 * are withheld from blinded roles such as the CRA, and a filter on them is answered with a 403, not an empty page —
 * `routes/site_subjects` rejects it first with `axon.accessDenied.inaccessibleFilterProperty` (route.c_list_site_subjects.js,
 * for callers outside its `unblindedRoles`), and Cortex's property ACLs would otherwise answer
 * `cortex.accessDenied.inaccessibleProperty`. Those roles search `c_number`, which every site role reads (SSP-4636).
 * The participant number prefix handling is shared by both branches.
 */
export const getParticipantSearchClause = (searchValue, canSearchByIdentifier) => {
    const idFragment = searchValue.toUpperCase().startsWith(`${PARTICIPANT_NUMBER_PREFIX}-`)
        ? searchValue.slice(PARTICIPANT_NUMBER_PREFIX.length + 1).trim()
        : '';
    const searchPattern = idFragment ? `/${escapeRegExp(idFragment)}/i` : `/^${escapeRegExp(searchValue)}/i`;
    return canSearchByIdentifier
        ? { $or: [{ c_search: { $regex: searchPattern } }, { c_username: { $regex: searchPattern } }] }
        : { c_number: { $regex: searchPattern } };
};
export var EParticipantCategory;
(function (EParticipantCategory) {
    EParticipantCategory["ACTIVE"] = "Active";
    EParticipantCategory["DEACTIVATED"] = "Deactivated";
})(EParticipantCategory || (EParticipantCategory = {}));
export const useFetchParticipants = ({ initialPage = 0, initialPageSize = 10, initialCategory = EParticipantCategory.ACTIVE, }) => {
    const { environment } = useCurrentEnvironment();
    const { participantStatusMap } = useContext(OrgContext);
    const { site } = useContext(SiteContext);
    const { getUserSelectedLocale } = useContext(UserLocaleContext);
    const { addNotification } = useToastNotification();
    const translate = useLocalizeMessage();
    const { isUserPermitted } = useLumosAuth();
    // Fails closed: false until a Cortex role resolves, and the c_number filter is one every site role can run.
    const canSearchByIdentifier = isUserPermitted(LUMOS_PERMISSIONS.PARTICIPANTS_SEARCH_BY_IDENTIFIER);
    const userSelectedLocale = getUserSelectedLocale();
    const siteId = site?._id;
    const [totalPages, setTotalPages] = useState(0);
    const [totalItems, setTotalItems] = useState(0);
    const [pagination, setPagination] = useState({ pageIndex: initialPage, pageSize: initialPageSize });
    const [paginationSiteId, setPaginationSiteId] = useState(siteId);
    const [searchValue, setSearchValue] = useState(undefined);
    const [category, setCategory] = useState(initialCategory);
    // Reset during render (not in an effect) so the first request for the new site uses skip=0.
    // An effect would still fire the previous page's skip against the new site first.
    if (siteId !== paginationSiteId) {
        setPaginationSiteId(siteId);
        setPagination((previous) => (previous.pageIndex === 0 ? previous : { ...previous, pageIndex: 0 }));
    }
    const participantStatuses = useMemo(() => {
        return Array.from(participantStatusMap.keys());
    }, [participantStatusMap]);
    const fetchParticipants = async (params) => {
        const { pageSize, pageIndex, filters } = params;
        if (!environment) {
            return [];
        }
        const activeParticipantStatuses = participantStatuses.filter((status) => status !== EParticipantCategory.DEACTIVATED);
        const whereClause = {
            c_type: { $in: ['participant', null] },
            c_status: { $in: activeParticipantStatuses },
        };
        const searchValue = filters.searchValue?.trim();
        if (searchValue) {
            Object.assign(whereClause, getParticipantSearchClause(searchValue, canSearchByIdentifier));
        }
        if (filters.category === EParticipantCategory.DEACTIVATED) {
            whereClause.c_status = { $eq: filters.category };
        }
        try {
            const { data: response } = await getApiInstance(environment, userSelectedLocale).get(`/v2/routes/site_subjects/${site?._id}`, {
                params: {
                    enablePagination: true,
                    sort: JSON.stringify({ created: -1 }),
                    where: JSON.stringify(whereClause),
                    limit: pageSize,
                    skip: pageIndex * pageSize,
                },
            });
            // the response is different from other axon endpoints b/c we needed to put total number of objects in the response
            if (response?.data?.data?.length === 0) {
                setTotalPages(0);
                setTotalItems(0);
                return [];
            }
            setTotalItems(response?.data?.total || 0);
            setTotalPages(Math.ceil((response?.data?.total || 0) / pagination.pageSize));
            return response?.data?.data || [];
        }
        catch (error) {
            const message = getApiErrorMessage(error);
            addNotification({
                title: '',
                subtitle: message ?? translate('error.generic'),
                type: 'error',
            });
            return [];
        }
    };
    const { data: participants, isLoading, isFetching, } = useQuery({
        queryKey: [
            'participantsList',
            environment?.id,
            site?._id,
            userSelectedLocale,
            // The search branch is part of the key so a search issued before the roles resolved refetches once they
            // do; with no search the where clause does not depend on the role, so role resolution must not refetch.
            { pagination, searchValue, category, searchByIdentifier: searchValue ? canSearchByIdentifier : null },
        ],
        queryFn: () => fetchParticipants({
            pageSize: pagination.pageSize,
            pageIndex: pagination.pageIndex,
            filters: { searchValue, category },
        }),
        enabled: true,
        placeholderData: keepPreviousData,
    });
    return {
        isLoading,
        isFetching,
        participants,
        totalPages,
        totalItems,
        pagination,
        setPagination,
        setSearchValue,
        setCategory,
    };
};
